/**
 * Links the "Periode performa" tabs to the date-range filter: each tab owns a
 * concrete date range, and a range that matches a tab re-selects that tab.
 */
import {
  formatMonthSpan,
  formatRangeCaption,
  formatRangeCompact,
  presetRange,
  rangeLengthDays,
  sameDay,
  startOfDay,
  type DateRange,
} from '@/lib/date-range'
import type { PerformancePeriod, ReportAggregation } from './schema'

interface PeriodConfig {
  id: PerformancePeriod
  /** Tab text. */
  label: string
  /** Caption prefix, e.g. "7 hari terakhir". */
  rangeLabel: string
  /** Bucket size the API is asked for when this tab is active. */
  aggregation: ReportAggregation
  range: (today: Date) => DateRange
  formatDates: (range: DateRange) => string
}

/** Months covered by the "Bulanan" tab, including the current month. */
const MONTHLY_SPAN = 6

export const performancePeriods: PeriodConfig[] = [
  {
    id: 'daily',
    label: 'Harian',
    rangeLabel: 'Hari ini',
    aggregation: 'day',
    range: (today) => presetRange('today', today)!,
    formatDates: formatRangeCompact,
  },
  {
    id: 'weekly',
    label: 'Mingguan',
    rangeLabel: '7 hari terakhir',
    aggregation: 'week',
    range: (today) => presetRange('last7', today)!,
    formatDates: formatRangeCompact,
  },
  {
    id: 'monthly',
    label: 'Bulanan',
    rangeLabel: `${MONTHLY_SPAN} bulan terakhir`,
    aggregation: 'month',
    range: (today) => {
      const end = startOfDay(today)
      return { start: new Date(end.getFullYear(), end.getMonth() - (MONTHLY_SPAN - 1), 1), end }
    },
    formatDates: formatMonthSpan,
  },
]

/** Longest range (in days) still charted per day. */
const DAY_AGGREGATION_MAX_DAYS = 31
/** Longest range (in days) still charted per week; anything longer goes per month. */
const WEEK_AGGREGATION_MAX_DAYS = 120

/**
 * Bucket size for a range. A range that matches a tab uses that tab's
 * aggregation (Harian `day`, Mingguan `week`, Bulanan `month`). Any other range
 * is sized by length to keep a readable number of bars: up to 31 days per day,
 * up to 120 days per week, longer per month. The API only accepts `day`,
 * `week`, and `month` (WEBLP-3265).
 */
export function aggregationForRange(range: DateRange, today: Date): ReportAggregation {
  const tab = matchPeriod(range, today)
  if (tab) return tab.aggregation

  const days = rangeLengthDays(range)
  if (days <= DAY_AGGREGATION_MAX_DAYS) return 'day'
  if (days <= WEEK_AGGREGATION_MAX_DAYS) return 'week'
  return 'month'
}

export function periodRange(id: PerformancePeriod, today: Date): DateRange {
  const config = performancePeriods.find((period) => period.id === id) ?? performancePeriods[0]!
  return config.range(today)
}

/** The tab whose range exactly matches `range`, or `null` for any other range. */
export function matchPeriod(range: DateRange, today: Date): PeriodConfig | null {
  return (
    performancePeriods.find((period) => {
      const candidate = period.range(today)
      return sameDay(candidate.start, range.start) && sameDay(candidate.end, range.end)
    }) ?? null
  )
}

/** "Hari ini · 3 Nov 2026" for a tab range; other ranges use the date-filter caption. */
export function periodCaption(range: DateRange, today: Date): string {
  const period = matchPeriod(range, today)
  if (!period) return formatRangeCaption(range, today)
  return `${period.rangeLabel} · ${period.formatDates(range)}`
}
